import { useGetAutopilotTasksForCampaign } from '@urbanx/agx.activities.client';
import { useGetAgencyDetails, useStaffGetAllUsersInformation } from '@urbanx/agx.agencies.client';
import { useStaffGetCampaign, useStaffGetContract } from '@urbanx/agx.campaigns.client';
import { useGetAllStaff } from '@urbanx/agx.staff.client';
import {
  AutomationFailureResponseStatus,
  AutomationFailureResponseUserType,
  type AutomationFailureResponse,
} from '@urbanx/agx.support.client';
import {
  AgxButton,
  AgxNotificationBox,
  AgxSpacer,
  AgxTypography,
  Icon_CheckmarkCircleOutline,
} from '@urbanx/agx.ui-components2';
import { Pages } from 'constants/menu';
import { Link } from 'react-router-dom';
import { getTaskLink } from 'utils/tasks/useNavigateToTask';
import AutomationFailureDetailRow from './AutomationFailureDetailRow';
import AutomationFailureDiagnostics from './AutomationFailureDiagnostics';
import AutomationFailureResolutionHistory from './AutomationFailureResolutionHistory';
import {
  formatAutomationContextName,
  formatAutomationContextValue,
  formatAutomationFailureInstant,
  formatAutomationFailureRelativeInstant,
  getAutomationFailureDiagnosticEntries,
  getAutomationReasonExplanation,
  getAutomationResolutionLabel,
} from './automationFailurePresentation';

interface AutomationFailureDetailBodyProps {
  failure: AutomationFailureResponse;
}

const AutomationFailureDetailBody = ({ failure }: AutomationFailureDetailBodyProps) => {
  const { data: agency, isPending: agencyPending } = useGetAgencyDetails(
    failure.agencyId ?? '',
    !!failure.agencyId
  );
  const { data: campaign, isPending: campaignPending } = useStaffGetCampaign(
    failure.campaignId ?? '',
    !!failure.campaignId
  );
  const { data: contract, isPending: contractPending } = useStaffGetContract(
    failure.contractId ?? '',
    !!failure.contractId
  );
  const { data: tasks, isPending: tasksPending } = useGetAutopilotTasksForCampaign(
    failure.campaignId ?? '',
    failure.contractId,
    !!failure.campaignId && !!failure.taskId
  );
  const task = tasks?.find(({ id }) => id === failure.taskId);
  const taskLink = task ? getTaskLink(task) : undefined;
  const originatingUserIsAgent = failure.userType === AutomationFailureResponseUserType.Agent;
  const originatingUserIsStaff = failure.userType === AutomationFailureResponseUserType.Staff;
  const { data: users, isPending: usersPending } = useStaffGetAllUsersInformation(
    !!failure.userId && originatingUserIsAgent
  );
  const staffIds = [
    ...(failure.resolutionHistory ?? []).flatMap(({ staffUserId, reopenedByStaffUserId }) =>
      [staffUserId, reopenedByStaffUserId].filter((id): id is string => !!id)
    ),
    ...(failure.userId && originatingUserIsStaff ? [failure.userId] : []),
  ];
  const { data: staffMembers, isPending: staffPending } = useGetAllStaff(staffIds.length > 0);

  const originatingUserLink =
    originatingUserIsStaff && failure.userId
      ? `${Pages.EDIT_STAFF}/${encodeURIComponent(failure.userId)}`
      : originatingUserIsAgent && failure.agencyId && failure.userId
        ? `/${Pages.AGENCIES}/${failure.agencyId}/${Pages.TEAMS}/${Pages.TEAMS_MEMBERS}/edit/${failure.userId}`
        : undefined;

  const getStaffName = (staffUserId?: string) => {
    if (!staffUserId) return 'Unknown staff member';
    if (staffPending) return 'Loading staff member...';

    const staffMember = staffMembers?.find(({ id }) => id === staffUserId);
    return [staffMember?.firstName, staffMember?.lastName].filter(Boolean).join(' ') || staffUserId;
  };

  const getOriginatingUserName = () => {
    if (!failure.userId) return '';

    if (originatingUserIsAgent) {
      if (usersPending) return 'Loading agent...';
      const user = users?.find(({ userId }) => userId === failure.userId);
      const name =
        user?.name?.fullName ||
        [user?.name?.firstName, user?.name?.lastName].filter(Boolean).join(' ');
      return name || failure.userId;
    }

    return originatingUserIsStaff ? getStaffName(failure.userId) : failure.userId;
  };

  const usefulAttributes = (failure.attributes ?? []).filter(
    ({ name }) => !name || !['AgencyId', 'CampaignId', 'ContractId', 'TaskId'].includes(name)
  );
  const latestResolution = failure.resolutionHistory?.at(-1);

  return (
    <AgxSpacer
      direction="vertical"
      gap="xs"
      padding={{ top: 'xs', bottom: 'lg', left: 'xl', right: 'xl' }}
    >
      {failure.status === AutomationFailureResponseStatus.Resolved ? (
        <AgxSpacer
          direction="horizontal"
          gap="spacing-md"
          padding="spacing-xl"
          contentAlignment="center"
          fill
          style={{
            boxSizing: 'border-box',
            borderRadius: 'var(--radius-xl)',
            backgroundColor: 'var(--bg-success)',
          }}
        >
          <Icon_CheckmarkCircleOutline height={18} width={18} color="var(--fg-success)" />
          <AgxTypography
            id="automation-failure-resolution-summary"
            type="body_ui"
            size="small"
            color="var(--text-success)"
          >
            {latestResolution
              ? `${getStaffName(latestResolution.staffUserId)} marked this as ${getAutomationResolutionLabel(latestResolution.type).toLowerCase()} on ${formatAutomationFailureInstant(latestResolution.resolvedUtc)}.`
              : 'This automation failure has been resolved.'}
          </AgxTypography>
        </AgxSpacer>
      ) : (
        <div className="automationFailureWarning">
          <AgxNotificationBox
            type="warning"
            heading={getAutomationReasonExplanation(failure.reason)}
            body={failure.detail ? `Reason: ${failure.detail}` : null}
          />
        </div>
      )}

      <AgxSpacer
        direction="vertical"
        fill
        style={{
          boxSizing: 'border-box',
          padding: 'var(--spacing-md) var(--spacing-3xl)',
          borderRadius: 'var(--radius-xl)',
          background: 'var(--bg-primary)',
        }}
      >
        {(failure.campaignAddress || failure.campaignId) && (
          <AutomationFailureDetailRow id="automation-failure-campaign" label="Campaign">
            {campaignPending ? (
              'Loading campaign...'
            ) : campaign && failure.agencyId && failure.campaignId ? (
              <AgxButton
                id="automation-failure-open-campaign"
                variant="link"
                size="small"
                className="automationFailureLink"
                text={failure.campaignAddress ?? failure.campaignId}
                renderLink={(props) => (
                  <Link
                    to={`/${Pages.AGENCIES}/${failure.agencyId}/${Pages.CAMPAIGNS}/${failure.campaignId}/${Pages.CAMPAIGN_OVERVIEW}`}
                    {...props}
                  />
                )}
              />
            ) : (
              (failure.campaignAddress ?? failure.campaignId)
            )}
          </AutomationFailureDetailRow>
        )}

        {(failure.agencyName || failure.agencyId) && (
          <AutomationFailureDetailRow id="automation-failure-agency" label="Agency">
            {agencyPending ? (
              'Loading agency...'
            ) : agency && failure.agencyId ? (
              <AgxButton
                id="automation-failure-open-agency"
                variant="link"
                size="small"
                className="automationFailureLink"
                text={failure.agencyName ?? agency.name ?? failure.agencyId}
                renderLink={(props) => (
                  <Link
                    to={`/${Pages.AGENCIES}/${failure.agencyId}/${Pages.AGENCY_DETAILS}`}
                    {...props}
                  />
                )}
              />
            ) : (
              (failure.agencyName ?? failure.agencyId)
            )}
          </AutomationFailureDetailRow>
        )}

        {failure.userId && (
          <AutomationFailureDetailRow
            id="automation-failure-user"
            label={originatingUserIsAgent ? 'Agent' : originatingUserIsStaff ? 'Staff' : 'User'}
          >
            {originatingUserIsAgent || originatingUserIsStaff ? (
              <AgxButton
                id="automation-failure-open-user"
                variant="link"
                size="small"
                className="automationFailureLink"
                text={getOriginatingUserName()}
                renderLink={
                  originatingUserLink
                    ? (props) => <Link to={originatingUserLink} {...props} />
                    : undefined
                }
              />
            ) : (
              failure.userId
            )}
          </AutomationFailureDetailRow>
        )}

        {failure.contractId && (
          <AutomationFailureDetailRow id="automation-failure-contract" label="Contract">
            {contractPending ? (
              'Loading contract...'
            ) : contract && failure.agencyId && failure.campaignId ? (
              <AgxButton
                id="automation-failure-open-contract"
                variant="link"
                size="small"
                className="automationFailureLink"
                text={contract.contractName ?? failure.contractId}
                renderLink={(props) => (
                  <Link
                    to={`/${Pages.AGENCIES}/${failure.agencyId}/${Pages.CAMPAIGNS}/${failure.campaignId}/${Pages.CONTRACTS}/${failure.contractId}/${Pages.SALE_INFORMATION}`}
                    {...props}
                  />
                )}
              />
            ) : (
              failure.contractId
            )}
          </AutomationFailureDetailRow>
        )}

        {failure.taskId && (
          <AutomationFailureDetailRow id="automation-failure-task" label="Task">
            {tasksPending ? (
              'Loading task...'
            ) : task ? (
              <AgxButton
                id="automation-failure-open-task"
                variant="link"
                size="small"
                className="automationFailureLink"
                text={task.displayTitle ?? failure.taskId}
                renderLink={taskLink ? (props) => <Link to={taskLink} {...props} /> : undefined}
              />
            ) : (
              failure.taskId
            )}
          </AutomationFailureDetailRow>
        )}

        {usefulAttributes.map((attribute, index) => (
          <AutomationFailureDetailRow
            key={`${attribute.name ?? 'detail'}-${index}`}
            id={`automation-failure-attribute-${index}`}
            label={formatAutomationContextName(attribute.name)}
          >
            {formatAutomationContextValue(attribute)}
          </AutomationFailureDetailRow>
        ))}

        <AutomationFailureDetailRow id="automation-failure-occurred" label="Occurred">
          {failure.occurredUtc
            ? `${formatAutomationFailureInstant(failure.occurredUtc)} · ${formatAutomationFailureRelativeInstant(failure.occurredUtc)}`
            : formatAutomationFailureInstant()}
        </AutomationFailureDetailRow>
      </AgxSpacer>

      {!!failure.resolutionHistory?.length && (
        <AutomationFailureResolutionHistory
          history={failure.resolutionHistory}
          getStaffName={getStaffName}
        />
      )}

      <AutomationFailureDiagnostics entries={getAutomationFailureDiagnosticEntries(failure)} />
    </AgxSpacer>
  );
};

export default AutomationFailureDetailBody;
